<!-- components/CreateRoomModal.vue -->
<script setup lang="ts">
import { ref } from 'vue';
import { X } from 'lucide-vue-next';
import {
  DifficultyLevel,
  Language,
  TagKey,
  useTranslations,
} from '@/composables/useTranslations';
import { ref as dbRef, push, set } from 'firebase/database';
import { db, roomsPath } from '../configs/firebase';
import {
  LANGUAGE_KEYS,
  LANGUAGE_EMOJIS,
  DIFFICULTY_KEYS,
  TAG_KEYS,
} from '@/utils/constants';
import { Room } from '../interfaces/Room.interface';
import { useState } from '../composables/useState';

interface Props {
  modelValue: boolean;
  defaultLanguage?: Language;
  defaultDifficulty?: DifficultyLevel;
}

const props = defineProps<Props>();

const t = useTranslations();
const { username } = useState();
const emit = defineEmits(['update:modelValue']);

const MAX_TAGS = 3;
const MAX_TITLE_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;

const selectedTags = ref<TagKey[]>([]);
const title = ref('');
const description = ref('');
const language = ref(props.defaultLanguage || LANGUAGE_KEYS[1]);
const difficulty = ref(props.defaultDifficulty || DIFFICULTY_KEYS[1]);

const handleSubmit = async () => {
  try {
    const newRoomRef = push(dbRef(db, roomsPath));

    if (!newRoomRef.key) {
      throw new Error('Failed to create room. Could not generate room ID.');
    }

    const newRoom: Room = {
      id: newRoomRef.key,
      title: title.value,
      description: description.value,
      language: language.value,
      difficulty: difficulty.value,
      tags: selectedTags.value,
      maxPeerCount: 5,
      createdBy: username.value ?? 'anonymous',
      createdAt: new Date().toISOString(),
    };

    await set(newRoomRef, newRoom);
  } catch (error) {
    console.error('Error creating room:', error);
  } finally {
    selectedTags.value = [];
    closeModal();
  }
};

const handleTagClick = (tag: TagKey) => {
  if (selectedTags.value.includes(tag)) {
    selectedTags.value = selectedTags.value.filter((t) => t !== tag);
  } else if (selectedTags.value.length < MAX_TAGS) {
    selectedTags.value.push(tag);
  }
};

const closeModal = () => {
  emit('update:modelValue', false);
};
</script>

<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex min-h-full items-center justify-center p-4 text-center">
      <div class="fixed inset-0 bg-black/25" />

      <div
        class="relative w-full max-w-2xl transform rounded-2xl bg-white p-6 text-left shadow-xl transition-all"
      >
        <div class="absolute right-4 top-4">
          <button
            class="rounded-lg p-2 hover:bg-gray-100 transition-colors"
            @click="closeModal"
          >
            <X class="h-6 w-6 text-secondary" />
          </button>
        </div>

        <h3 class="text-2xl font-bold mb-6">{{ t.createRoom }}</h3>

        <form class="space-y-6" @submit.prevent="handleSubmit">
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ t.title }}
              <span class="text-secondary ml-1">
                ({{
                  t.charactersMax.replace('{0}', MAX_TITLE_LENGTH.toString())
                }})
              </span>
            </label>
            <input
              v-model="title"
              type="text"
              required
              :maxlength="MAX_TITLE_LENGTH"
              :placeholder="t.titlePlaceholder"
              class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">
              {{ t.description }}
              <span class="text-secondary ml-1">
                ({{
                  t.charactersMax.replace(
                    '{0}',
                    MAX_DESCRIPTION_LENGTH.toString(),
                  )
                }})
              </span>
            </label>
            <textarea
              v-model="description"
              required
              :maxlength="MAX_DESCRIPTION_LENGTH"
              :placeholder="t.descriptionPlaceholder"
              class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
              rows="2"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">
                {{ t.language }}
              </label>
              <select
                v-model="language"
                class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
              >
                <option v-for="lang in LANGUAGE_KEYS" :key="lang" :value="lang">
                  {{ LANGUAGE_EMOJIS[lang] }} {{ t[lang] }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">
                {{ t.difficulty }}
              </label>
              <select
                v-model="difficulty"
                class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
              >
                <option
                  v-for="level in DIFFICULTY_KEYS"
                  :key="level"
                  :value="level"
                >
                  {{ t[level] }}
                </option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              {{ t.tags }} ({{ t.maxTags.replace('{0}', MAX_TAGS.toString()) }})
            </label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="tag in TAG_KEYS"
                :key="tag"
                :class="[
                  'px-3 py-1 rounded-full text-sm transition-colors',
                  selectedTags.includes(tag)
                    ? 'bg-accent text-white'
                    : 'bg-gray-200 text-gray-700',
                  selectedTags.length >= MAX_TAGS && !selectedTags.includes(tag)
                    ? 'opacity-50 cursor-not-allowed'
                    : '',
                ]"
                type="button"
                @click="handleTagClick(tag)"
              >
                {{ t[tag] }}
              </button>
            </div>
          </div>

          <div class="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              class="px-6 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              @click="closeModal"
            >
              {{ t.cancel }}
            </button>
            <button
              type="submit"
              class="px-6 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
            >
              {{ t.createRoom }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
